/**
 * User Profile P1 — the ONE avatar for the tenant and platform apps.
 *
 * Renders the person's photo when there is one and their initials otherwise,
 * using theme tokens (never a hardcoded colour) so it follows tenant branding
 * and dark mode. Replaces the five inline initials implementations the audit
 * found. The customer portal keeps its own token system and its own avatar.
 */
const SIZE_CLASS = {
  sm: 'h-7 w-7 text-xs',
  md: 'h-9 w-9 text-sm',
  lg: 'h-16 w-16 text-xl',
  xl: 'h-24 w-24 text-3xl',
} as const;

export type AvatarSize = keyof typeof SIZE_CLASS;

export function initialsOf(firstName?: string | null, lastName?: string | null): string {
  const value = `${firstName?.trim()[0] ?? ''}${lastName?.trim()[0] ?? ''}`.toUpperCase();
  return value || 'U';
}

export interface AvatarProps {
  firstName?: string | null;
  lastName?: string | null;
  /** A resolved photo URL; falsy renders initials. */
  src?: string | null;
  size?: AvatarSize;
  className?: string;
  /** Accessible name; defaults to the full name. Pass '' to hide from AT. */
  alt?: string;
}

export function Avatar({
  firstName,
  lastName,
  src,
  size = 'md',
  className = '',
  alt,
}: AvatarProps) {
  const name = `${firstName ?? ''} ${lastName ?? ''}`.trim();
  const label = alt ?? name;
  const base = `${SIZE_CLASS[size]} shrink-0 rounded-full ${className}`;
  if (src) {
    return (
      <img
        src={src}
        alt={label}
        data-testid="avatar-photo"
        className={`${base} border border-border object-cover`}
      />
    );
  }
  return (
    <div
      data-testid="avatar-initials"
      role={label ? 'img' : undefined}
      aria-label={label || undefined}
      aria-hidden={label ? undefined : true}
      className={`${base} flex items-center justify-center bg-primary font-medium text-primary-foreground`}
    >
      {initialsOf(firstName, lastName)}
    </div>
  );
}
